"use client";

import { ButtonHTMLAttributes, CSSProperties, FC, ReactNode, useState } from "react";

/**
 * GROWTH:CODE 共通プライマリボタン
 *
 * 仕様: docs/spec/design_spec.md §3.2 / §6.3 / §4.4
 *   - 背景 #FFFFFF / 枠 1px solid #D6D6D6 / 文字色 #3A3A3A
 *   - フォント 16px / letter-spacing 0.5em (押し出し感)
 *   - 角丸 2px / padding 8px 24px / 高さ 48px (タップ最小 44px → 推奨 48px)
 *   - ホバー: 影濃く / 押下: 影縮小 + 背景 #F8F8F8
 *
 * variant:
 *   - 'default'  : 仕様準拠の白背景
 *   - 'filled'   : 仕様トーンを保ちつつ強調 (text-primary 背景)
 *   - 'danger'   : ログアウト等の破壊的操作
 */
type PrimaryButtonVariant = "default" | "filled" | "danger";

interface PrimaryButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "style"> {
  children: ReactNode;
  variant?: PrimaryButtonVariant;
  /** 横幅 100% */
  block?: boolean;
  /** 文字間隔。'wide' = 0.5em (仕様) / 'normal' = 0.05em (狭いラベル用) */
  spacing?: "wide" | "normal";
  /** 高さ (px)。デフォルト 48 (仕様 §4.4) */
  height?: number;
  /** Loading 表示 (簡易) */
  loading?: boolean;
  style?: CSSProperties;
}

interface VariantTones {
  bg: string;
  bgHover: string;
  bgPressed: string;
  color: string;
  borderColor: string;
}

const TONES: Record<PrimaryButtonVariant, VariantTones> = {
  default: {
    bg: "var(--color-bg-base, #ffffff)",
    bgHover: "var(--color-bg-base, #ffffff)",
    bgPressed: "#f8f8f8",
    color: "var(--color-text-primary, #3a3a3a)",
    borderColor: "var(--color-border-soft, #d6d6d6)",
  },
  filled: {
    bg: "var(--color-text-primary, #3a3a3a)",
    bgHover: "var(--color-text-primary, #3a3a3a)",
    bgPressed: "#2a2a2a",
    color: "#ffffff",
    borderColor: "var(--color-text-primary, #3a3a3a)",
  },
  danger: {
    bg: "#ffffff",
    bgHover: "#ffffff",
    bgPressed: "#fbecea",
    color: "#c0392b",
    borderColor: "#e6b6b0",
  },
};

const DISABLED: VariantTones = {
  bg: "#f3f3f3",
  bgHover: "#f3f3f3",
  bgPressed: "#f3f3f3",
  color: "#bdbdbd",
  borderColor: "#e6e6e6",
};

const PrimaryButton: FC<PrimaryButtonProps> = ({
  children,
  variant = "default",
  block = false,
  spacing = "wide",
  height = 48,
  loading = false,
  disabled = false,
  style,
  ...rest
}) => {
  const isDisabled = disabled || loading;
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  const tones = isDisabled ? DISABLED : TONES[variant];
  const currentBg = pressed ? tones.bgPressed : hovered ? tones.bgHover : tones.bg;
  // §3.4: 光源左上 → 影は右下
  const shadowDefault = "none";
  const shadowHover = "var(--shadow-card, 4px 6px 16px rgba(0,0,0,0.06))";
  const shadowPressed = "var(--shadow-button-pressed, 2px 3px 8px rgba(0,0,0,0.08))";
  const currentShadow = isDisabled
    ? shadowDefault
    : pressed
    ? shadowPressed
    : hovered
    ? shadowHover
    : shadowDefault;

  const base: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    width: block ? "100%" : undefined,
    height,
    padding: "0 24px",
    fontFamily: "var(--font-body)",
    fontSize: 16,
    fontWeight: 500,
    letterSpacing: spacing === "wide" ? "0.5em" : "0.05em",
    // 0.5em の letter-spacing は最後の文字後にも余白を付けるため左にずれる。
    // テキストを中央に見せるために textIndent でその分だけ右に押し出す。
    textIndent: spacing === "wide" ? "0.5em" : 0,
    border: "1px solid",
    borderRadius: "var(--radius-button, 2px)",
    transition:
      "box-shadow 120ms ease, background-color 120ms ease, color 120ms ease",
    cursor: isDisabled ? "not-allowed" : "pointer",
    whiteSpace: "nowrap",
    userSelect: "none",
    backgroundColor: currentBg,
    color: tones.color,
    borderColor: tones.borderColor,
    boxShadow: currentShadow,
  };

  return (
    <button
      {...rest}
      disabled={isDisabled}
      style={{ ...base, ...style }}
      onMouseEnter={(e) => {
        setHovered(true);
        rest.onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        setHovered(false);
        setPressed(false);
        rest.onMouseLeave?.(e);
      }}
      onMouseDown={(e) => {
        if (!isDisabled) setPressed(true);
        rest.onMouseDown?.(e);
      }}
      onMouseUp={(e) => {
        setPressed(false);
        rest.onMouseUp?.(e);
      }}
      onTouchStart={(e) => {
        if (!isDisabled) setPressed(true);
        rest.onTouchStart?.(e);
      }}
      onTouchEnd={(e) => {
        setPressed(false);
        rest.onTouchEnd?.(e);
      }}
    >
      {loading ? "…" : children}
    </button>
  );
};

export default PrimaryButton;
